// ============================================================
// TMR Portal Admin — aba "Regras de negócio" dentro do detalhe da empresa (25)
// ============================================================
import { useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Notice } from '../../components/Notice';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons } from '../../lib/icons';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import {
  useCompanyBusinessRules,
  useRemoveCompanyRuleOverride,
  useSetCompanyRuleOverride,
} from '../../data/businessRules/queries';
import {
  companyRuleSituation,
  overrideIneligibility,
  ruleRangeLabel,
  ruleReach,
  ruleValueLabel,
} from '../../data/businessRules/status';
import type { CompanyBusinessRule } from '../../data/businessRules/contract';
import { RuleValueModal } from './RuleValueModal';

/** Props for {@link CompanyBusinessRulesTab}. */
interface CompanyBusinessRulesTabProps {
  companyId: string;
}

/**
 * One Empresa's business rules: the TMR's default, the Empresa's own value when it has one, and the value its
 * sales use (`25` §3.2).
 *
 * An Empresa not yet replicated to the legacy cannot have an exception — the v1.1 finds one by producer id —
 * and the tab says so in place of the actions, instead of offering one the server refuses.
 */
export function CompanyBusinessRulesTab({ companyId }: CompanyBusinessRulesTabProps) {
  const [editing, setEditing] = useState<CompanyBusinessRule | null>(null);
  const [removing, setRemoving] = useState<CompanyBusinessRule | null>(null);

  const canRead = usePermission(PERMISSIONS.companyBusinessRulesRead);
  const canManage = usePermission(PERMISSIONS.companyBusinessRulesManage);

  const query = useCompanyBusinessRules(canRead ? companyId : undefined);
  const set = useSetCompanyRuleOverride(companyId);
  const remove = useRemoveCompanyRuleOverride(companyId);

  // Without company.business_rules.read the read would 403. Saying so beats an error state that reads like a
  // fault — the server is right to refuse (06 §6).
  if (!canRead) {
    return (
      <div className="pa-card">
        <EmptyState
          message="Você não tem permissão para ver as regras de negócio desta empresa."
          hint="Peça o acesso a quem administra os papéis."
        />
      </div>
    );
  }

  if (query.isPending) return <Skeleton rows={3} />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  const ineligible = overrideIneligibility(query.data);
  const actionable = canManage && ineligible === null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {ineligible && <Notice tone="warning">{ineligible}</Notice>}

      {remove.error && (
        <Notice tone="danger">
          {remove.error instanceof ApiError ? remove.error.detail : 'Não foi possível remover a exceção.'}
        </Notice>
      )}

      {query.data.items.length === 0 ? (
        <div className="pa-card">
          <EmptyState message="Nenhuma regra de negócio cadastrada." />
        </div>
      ) : (
        <div className="pa-card" style={{ padding: 0, overflow: 'hidden' }}>
          {query.data.items.map((item) => (
            <CompanyRuleRow
              key={item.key}
              item={item}
              actionable={actionable}
              onEdit={() => setEditing(item)}
              onRemove={() => setRemoving(item)}
            />
          ))}
        </div>
      )}

      {editing && (
        <RuleValueModal
          title={editing.overridden ? 'Alterar a exceção' : 'Definir exceção'}
          rule={editing}
          initialValue={editing.effectiveValue}
          reach={ruleReach(editing, 'override')}
          confirmLabel="Salvar exceção"
          onSave={(value) => set.mutateAsync({ key: editing.key, value })}
          onClose={() => setEditing(null)}
        />
      )}

      {removing && (
        <ConfirmDialog
          title="Remover a exceção?"
          message={`A empresa volta a seguir o padrão da TMR: ${ruleValueLabel(removing, removing.defaultValue)}.`}
          confirmLabel="Remover exceção"
          danger
          icon={Icons.alert}
          // A refusal closes the dialog and surfaces in the notice above, where it stays readable.
          onConfirm={() => remove.mutateAsync(removing.key).catch(() => undefined)}
          onClose={() => setRemoving(null)}
        />
      )}
    </div>
  );
}

/** Props for {@link CompanyRuleRow}. */
interface CompanyRuleRowProps {
  item: CompanyBusinessRule;
  /** Whether the operator may change it here — the permission and a replicated Empresa. */
  actionable: boolean;
  onEdit: () => void;
  onRemove: () => void;
}

/** One rule as it applies to the Empresa: the value in force first, then whose it is. */
function CompanyRuleRow({ item, actionable, onEdit, onRemove }: CompanyRuleRowProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 20,
        padding: '18px 20px',
        borderBottom: '1px solid var(--border-default)',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 360px', minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink-900)' }}>{item.name}</div>
        <p style={{ margin: '4px 0 0', fontSize: 13.5, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
          {item.description}
        </p>
        <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--fg-subtle)' }}>
          {companyRuleSituation(item)} · Padrão da TMR: {ruleValueLabel(item, item.defaultValue)} · {ruleRangeLabel(item)}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, color: 'var(--ink-900)', whiteSpace: 'nowrap' }}>
          {ruleValueLabel(item, item.effectiveValue)}
        </span>
        {actionable && (
          <Btn variant="secondary" size="sm" icon={Icons.pen} onClick={onEdit}>
            {item.overridden ? 'Alterar exceção' : 'Definir exceção'}
          </Btn>
        )}
        {actionable && item.overridden && (
          <Btn variant="ghost" size="sm" icon={Icons.x} onClick={onRemove}>
            Remover exceção
          </Btn>
        )}
      </div>
    </div>
  );
}
